import React, { useContext, useRef } from 'react';
import { PlayerRefContext } from '../../context-provider';
import { resolveGroupGeometry } from '../../items/group/group-geometry';
import { EditorStarterItem } from '../../items/item-type';
import { changeItem } from '../../state/actions/change-item';
import {
  applyMovedKeyframes,
  captureKeyframeArrays,
  removeKeyframesAtFrame,
} from './keyframe-marker-utils';
import {
  useAllItems,
  useSelectedItems,
  useWriteContext,
} from '../../utils/use-context';
import { useCurrentPlayerFrame } from '../../utils/use-current-player-frame';

/**
 * 위치 키프레임 다이아 마커 — 인라인 SVG(에디터 그래픽). currentColor 라 text-* 로 색 제어.
 */
const MarkerDiamond: React.FC<{ className?: string }> = ({ className }) => (
  <svg
    viewBox="0 0 16 16"
    className={className}
    aria-hidden="true"
  >
    <path
      d="M8 1 15 8 8 15 1 8Z"
      fill="currentColor"
    />
  </svg>
);

/**
 * 선택된 아이템의 타임라인 위에 키프레임(위치·줌·페이지 union)을 다이아로 표시한다.
 * CapCut 식 조작: 클릭 = 그 프레임으로 시크, 가로 드래그 = 키프레임 시간 이동,
 * 우클릭 = 그 프레임의 키프레임 삭제(전 타입).
 * 플레이헤드 구독은 이 내부 컴포넌트에만 걸어(useCurrentPlayerFrame), 선택된 아이템 한 개의
 * 마커만 매 프레임 리렌더되게 한다.
 */
const SelectedKeyframeMarkers: React.FC<{
  item: EditorStarterItem;
  keyframes: { frame: number }[];
}> = ({ item, keyframes }) => {
  const playerRef = useContext(PlayerRefContext);
  const { items } = useAllItems();
  const { setState } = useWriteContext();
  const globalFrame = useCurrentPlayerFrame();
  const containerRef = useRef<HTMLDivElement>(null);
  // 드래그 여부 — 드래그로 끝난 pointer 시퀀스의 click(시크)을 무시하기 위함.
  const draggedRef = useRef(false);

  // 그룹은 from/duration 모두 geometry 가 SoT — durationInFrames 캐시는 sync 가
  // 건너뛰는 케이스(<=0)가 있어 쓰지 않는다. 매 프레임 리렌더라 memo 로 캐시.
  const groupGeometry = React.useMemo(
    () =>
      item.type === 'group' ? resolveGroupGeometry(item, items) : null,
    [item, items],
  );
  const from = groupGeometry ? groupGeometry.from : item.from;
  const duration = groupGeometry
    ? groupGeometry.durationInFrames
    : item.durationInFrames;
  const localFrame = Math.round(globalFrame - from);

  const itemId = item.id;

  const startDrag = (event: React.PointerEvent, origFrame: number): void => {
    if (event.button !== 0) return;
    event.stopPropagation();
    const containerWidth = containerRef.current?.offsetWidth ?? 0;
    if (containerWidth <= 0 || duration <= 0) return;
    const pxPerFrame = containerWidth / duration;
    const startX = event.clientX;
    draggedRef.current = false;
    let currentFrame = origFrame;
    // 드래그 시작 시점 스냅샷 — 매 스텝을 여기서 다시 계산해, 지나친 프레임의
    // 키프레임이 파괴되지 않게 한다(keyframe-marker-utils 참조).
    const snapshot = captureKeyframeArrays(item);

    const onPointerMove = (moveEvent: PointerEvent) => {
      const deltaPx = moveEvent.clientX - startX;
      if (!draggedRef.current && Math.abs(deltaPx) < 3) return;
      draggedRef.current = true;
      const nextFrame = Math.min(
        Math.max(0, Math.round(origFrame + deltaPx / pxPerFrame)),
        Math.max(0, duration - 1),
      );
      if (nextFrame === currentFrame) return;
      currentFrame = nextFrame;
      setState({
        update: (s) =>
          changeItem(s, itemId, (i) =>
            applyMovedKeyframes(i, snapshot, origFrame, nextFrame),
          ),
        commitToUndoStack: false,
      });
    };

    const onPointerUp = () => {
      window.removeEventListener('pointermove', onPointerMove);
      window.removeEventListener('pointerup', onPointerUp);
      if (draggedRef.current) {
        // 드래그 종료 시점의 상태를 undo 1회 단위로 확정
        setState({ update: (s) => s, commitToUndoStack: true });
        playerRef?.current?.seekTo(Math.round(from + currentFrame));
      }
    };

    window.addEventListener('pointermove', onPointerMove, { passive: true });
    window.addEventListener('pointerup', onPointerUp, { once: true });
  };

  return (
    <div
      ref={containerRef}
      className="pointer-events-none absolute inset-0 z-10"
    >
      {keyframes.map((keyframe) => {
        const leftPercent =
          duration > 0 ? (keyframe.frame / duration) * 100 : 0;
        const isActive = localFrame === Math.round(keyframe.frame);
        return (
          <button
            key={keyframe.frame}
            type="button"
            aria-label={`키프레임 ${keyframe.frame}프레임으로 이동`}
            title="클릭한 뒤 인스펙터에서 위치·줌 곡선을 선택할 수 있습니다."
            // 아이템 드래그는 pointerdown 구독 — mousedown 만 막으면 드래그 파이프라인
            // (커서 변경 + 빈 undo 엔트리)을 그대로 탄다. 둘 다 차단.
            onPointerDown={(event) => {
              event.stopPropagation();
              startDrag(event, Math.round(keyframe.frame));
            }}
            onMouseDown={(event) => event.stopPropagation()}
            onClick={(event) => {
              event.stopPropagation();
              if (draggedRef.current) return;
              playerRef?.current?.seekTo(Math.round(from + keyframe.frame));
            }}
            onContextMenu={(event) => {
              event.preventDefault();
              event.stopPropagation();
              const frame = Math.round(keyframe.frame);
              setState({
                update: (s) =>
                  changeItem(s, itemId, (i) =>
                    removeKeyframesAtFrame(i, frame),
                  ),
                commitToUndoStack: true,
              });
            }}
            style={{ left: `${leftPercent}%` }}
            className="pointer-events-auto absolute top-1/2 -translate-x-1/2 -translate-y-1/2 cursor-ew-resize p-5xs"
          >
            <MarkerDiamond
              className={`size-2 ${
                isActive
                  ? 'text-sys-cont-primary-default'
                  : 'text-sys-style-inverse-light'
              }`}
            />
          </button>
        );
      })}
    </div>
  );
};

/**
 * 선택 구독 계층 — 이 컴포넌트만 selection 을 구독한다. 키프레임이 있는 아이템에만
 * 마운트되므로(아래 TimelineItemKeyframeMarkers 참조), 선택 변경 시 리렌더 대상이
 * 전체 타임라인 아이템이 아니라 키프레임을 가진 소수로 제한된다.
 */
const KeyframeMarkersWhenSelected: React.FC<{
  item: EditorStarterItem;
  keyframes: { frame: number }[];
}> = ({ item, keyframes }) => {
  const { selectedItems } = useSelectedItems();

  if (!selectedItems.includes(item.id)) {
    return null;
  }

  return (
    <SelectedKeyframeMarkers
      item={item}
      keyframes={keyframes}
    />
  );
};

/**
 * TimelineItemContent 에서 모든 아이템에 마운트. 선택된 아이템에 키프레임(위치·줌)이
 * 있을 때만 마커를 렌더한다(비선택은 필름스트립/파형 위 시각 노이즈 방지·클릭 전 선택 선행).
 *
 * 성능: 키프레임이 없으면 selection 을 구독하지 않고 즉시 종료한다. 대부분의 아이템은
 * 여기서 끝나므로, memo 된 TimelineItemContent 서브트리가 selection 변경으로부터 격리된
 * 상태를 유지한다(키프레임을 가진 소수 아이템만 아래 계층에서 구독).
 */
export const TimelineItemKeyframeMarkers: React.FC<{
  item: EditorStarterItem;
}> = ({ item }) => {
  const positionKeyframes =
    'positionKeyframes' in item && Array.isArray(item.positionKeyframes)
      ? item.positionKeyframes
      : [];
  const zoomKeyframes =
    'zoomKeyframes' in item && Array.isArray(item.zoomKeyframes)
      ? item.zoomKeyframes
      : [];
  const pageKeyframes =
    'pageKeyframes' in item && Array.isArray(item.pageKeyframes)
      ? item.pageKeyframes
      : [];

  // 위치·줌·페이지 union — 같은(반올림) 프레임은 마커 하나로 합친다(key 충돌 방지).
  const frames = new Set<number>();
  const keyframes: { frame: number }[] = [];
  for (const keyframe of [
    ...positionKeyframes,
    ...zoomKeyframes,
    ...pageKeyframes,
  ]) {
    const frame = Math.round(keyframe.frame);
    if (frames.has(frame)) continue;
    frames.add(frame);
    keyframes.push({ frame });
  }
  keyframes.sort((a, b) => a.frame - b.frame);

  if (keyframes.length === 0) {
    return null;
  }

  return (
    <KeyframeMarkersWhenSelected
      item={item}
      keyframes={keyframes}
    />
  );
};
